import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { aplanarDetalle } from '@/lib/errores';
import '@/App.css';
import '@/styles/osagest-theme.css';
import LandingOSAGEST from '@/pages/LandingOSAGEST';
import Registro from '@/pages/Registro';
import SuperAdminLogin from '@/pages/SuperAdminLogin';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import LoginOSAGEST from '@/pages/LoginOSAGEST';
import ChangePassword from '@/pages/ChangePassword';
import Dashboard from '@/pages/Dashboard';
import Products from '@/pages/Products';
import Quotes from '@/pages/Quotes';
import Invoices from '@/pages/Invoices';
import SepaRemittances from '@/pages/SepaRemittances';
import Clients from '@/pages/Clients';
import VerifactuAdmin from '@/pages/VerifactuAdmin';
import EmailConfig from '@/pages/EmailConfig';
import ContabilidadConfig from '@/pages/ContabilidadConfig';
import ContabilidadAsientos from '@/pages/ContabilidadAsientos';
import FacturasRecibidas from '@/pages/FacturasRecibidas';
import ContabilidadLibros from '@/pages/ContabilidadLibros';
import ContabilidadFiscal from '@/pages/ContabilidadFiscal';
import OCRTickets from '@/pages/OCRTickets';
import Proveedores from '@/pages/Proveedores';
import ClientDetail from '@/pages/ClientDetail';
import CRM from '@/pages/CRM';
import Calendar from '@/pages/Calendar';
import Tickets from '@/pages/Tickets';
import TicketDetail from '@/pages/TicketDetail';
import Users from '@/pages/Users';
import CompanyConfig from '@/pages/CompanyConfig';
import SendTemplates from '@/pages/SendTemplates';
import Settings from '@/pages/Settings';
import Fichar from '@/pages/Fichar';
import MisRegistros from '@/pages/MisRegistros';
import EquipoFichajes from '@/pages/EquipoFichajes';
import { soloFicha } from '@/lib/fichajes';
import { GuardiaFichaje } from '@/components/GuardiaFichaje';
import Teamup from '@/pages/Teamup';
import Partners from '@/pages/Partners';
import Settlements from '@/pages/Settlements';
import Workflows from '@/pages/Workflows';
import Campaigns from '@/pages/Campaigns';
import CampaignDetail from '@/pages/CampaignDetail';
import LandingPackBarra from '@/pages/LandingPackBarra';
import LandingPackSala from '@/pages/LandingPackSala';
import LandingOfertaTodoEnUno from '@/pages/LandingOfertaTodoEnUno';
import ClientPortalLogin from '@/pages/ClientPortalLogin';
import ClientPortalDashboard from '@/pages/ClientPortalDashboard';
import ClientPortalInvoices from '@/pages/ClientPortalInvoices';
import ClientPortalTickets from '@/pages/ClientPortalTickets';
import ClientPortalQuotes from '@/pages/ClientPortalQuotes';
import { Toaster } from '@/components/ui/sonner';
import { ConfirmDialogRoot } from '@/lib/confirm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
});

axiosInstance.interceptors.request.use((config) => {
  // Check for client portal token first
  const clientToken = localStorage.getItem('client_portal_token') || sessionStorage.getItem('client_portal_token');
  if (clientToken) {
    config.headers.Authorization = `Bearer ${clientToken}`;
    return config;
  }
  
  // Otherwise use regular user token
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Que hace este interceptor con los errores:
//   1. 401 del portal de cliente -> a la pantalla de entrar.
//   2. `detail` siempre como TEXTO. FastAPI manda una lista de objetos en los
//      422 y los 111 sitios que pintan `detail` sin mirar se llevaban la
//      aplicacion por delante (error #31 de React = pantalla en blanco).
//      Ver `aplanarDetalle` en lib/errores.js.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    aplanarDetalle(error);
    if (error.response?.status === 401) {
      const clientToken = localStorage.getItem('client_portal_token') || sessionStorage.getItem('client_portal_token');
      if (clientToken) {
        // Clear client portal tokens
        localStorage.removeItem('client_portal_token');
        localStorage.removeItem('client_portal_user');
        sessionStorage.removeItem('client_portal_token');
        sessionStorage.removeItem('client_portal_user');
        window.location.href = '/portal-cliente/login';
      }
    }
    return Promise.reject(error);
  }
);

// Las paginas publicas (portal de cliente, landings) llaman con `axios` a
// secas, sin la instancia, asi que necesitan el mismo aplanado. Es inofensivo
// aplicarlo dos veces: si `detail` ya es texto, no hace nada.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    aplanarDetalle(error);
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  // Contraseña puesta por un administrador: no se entra a ningún sitio hasta
  // elegir una propia. La comprobación de verdad la hace el backend; esto solo
  // evita que la app se use con una credencial que conoce otra persona.
  if (localStorage.getItem('must_change_password') === '1') {
    return <Navigate to="/cambiar-password" />;
  }
  return children;
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axiosInstance.get('/auth/me')
        .then(res => {
          setUser(res.data);
          // El backend es la fuente de verdad del flag: si alguien limpia el
          // localStorage a mano, aquí se vuelve a poner.
          if (res.data?.must_change_password) {
            localStorage.setItem('must_change_password', '1');
          } else {
            localStorage.removeItem('must_change_password');
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          setUser(null);
        });
      
      // Load company config and apply corporate color
      axiosInstance.get('/company-config')
        .then(res => {
          if (res.data.corporate_color) {
            // Inject dynamic styles for corporate color
            const styleId = 'corporate-color-styles';
            let styleEl = document.getElementById(styleId);
            if (!styleEl) {
              styleEl = document.createElement('style');
              styleEl.id = styleId;
              document.head.appendChild(styleEl);
            }
            
            const color = res.data.corporate_color;
            styleEl.textContent = `
              :root {
                --corporate-color: ${color};
              }
              
              /* Apply ONLY to buttons (not sidebar, not navigation) */
              button[type="submit"]:not([class*="outline"]):not([class*="ghost"]):not([class*="secondary"]):not([class*="destructive"]),
              button.btn-primary,
              .osagest-btn-primary {
                background-color: ${color} !important;
              }
              
              button[type="submit"]:not([class*="outline"]):not([class*="ghost"]):not([class*="secondary"]):not([class*="destructive"]):hover,
              button.btn-primary:hover,
              .osagest-btn-primary:hover {
                filter: brightness(0.9);
              }
            `;
          }
        })
        .catch(err => console.error('Error loading company config'));
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <GuardiaFichaje user={user} />
        <Routes>
          <Route path="/" element={<LandingOSAGEST />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/super-admin/login" element={<SuperAdminLogin />} />
          <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
          <Route path="/login" element={<LoginOSAGEST setUser={setUser} />} />
          <Route path="/cambiar-password" element={<ChangePassword variant="user" setUser={setUser} />} />
          <Route path="/super-admin/cambiar-password" element={<ChangePassword variant="superadmin" />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                {/* Quien solo ficha no tiene panel: el backend le cierra todo
                    lo demás (fichajes_candado.py). */}
                {soloFicha(user) ? <Navigate to="/fichar" replace /> : <Dashboard user={user} />}
              </PrivateRoute>
            }
          />
          <Route
            path="/products"
            element={
              <PrivateRoute>
                <Products user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <PrivateRoute>
                <Quotes user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <PrivateRoute>
                <Invoices user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/sepa-remittances"
            element={
              <PrivateRoute>
                <SepaRemittances user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <PrivateRoute>
                <Clients user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <PrivateRoute>
                <ClientDetail user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/verifactu"
            element={
              <PrivateRoute>
                <VerifactuAdmin user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/email-config"
            element={
              <PrivateRoute>
                <EmailConfig user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/contabilidad/config"
            element={
              <PrivateRoute>
                <ContabilidadConfig user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/contabilidad/asientos"
            element={
              <PrivateRoute>
                <ContabilidadAsientos user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/contabilidad/facturas-recibidas"
            element={
              <PrivateRoute>
                <FacturasRecibidas user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/contabilidad/libros"
            element={
              <PrivateRoute>
                <ContabilidadLibros user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/contabilidad/fiscal"
            element={
              <PrivateRoute>
                <ContabilidadFiscal user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/contabilidad/ocr"
            element={
              <PrivateRoute>
                <OCRTickets user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/proveedores"
            element={
              <PrivateRoute>
                <Proveedores user={user} />
              </PrivateRoute>
            }
          />

          <Route
            path="/crm"
            element={
              <PrivateRoute>
                <CRM user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <PrivateRoute>
                <Calendar user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/tickets"
            element={
              <PrivateRoute>
                <Tickets user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/tickets/:ticketId"
            element={
              <PrivateRoute>
                <TicketDetail user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/teamup"
            element={
              <PrivateRoute>
                <Teamup user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/partners"
            element={
              <PrivateRoute>
                <Partners user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/settlements"
            element={
              <PrivateRoute>
                <Settlements user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/workflows"
            element={
              <PrivateRoute>
                <Workflows user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/campaigns"
            element={
              <PrivateRoute>
                <Campaigns user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/campaigns/:campaignId"
            element={
              <PrivateRoute>
                <CampaignDetail user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <Users user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/company-config"
            element={
              <PrivateRoute>
                <CompanyConfig user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/send-templates"
            element={
              <PrivateRoute>
                <SendTemplates user={user} />
              </PrivateRoute>
            }
          />
          {/* Registro de jornada (3-oct-2026). Sustituye a /time-clock (PIN
              en pantalla compartida, sin identificar a nadie) y /time-reports. */}
          <Route path="/fichar" element={<PrivateRoute><Fichar user={user} /></PrivateRoute>} />
          <Route path="/mis-registros" element={<PrivateRoute><MisRegistros user={user} /></PrivateRoute>} />
          <Route path="/equipo-fichajes" element={<PrivateRoute><EquipoFichajes user={user} /></PrivateRoute>} />
          <Route path="/time-clock" element={<Navigate to="/fichar" replace />} />
          <Route path="/time-reports" element={<Navigate to="/mis-registros" replace />} />
          {/* Public Landing Pages */}
          <Route path="/landing-pack-barra" element={<LandingPackBarra />} />
          <Route path="/landing-pack-sala" element={<LandingPackSala />} />
          <Route path="/landing-oferta-todo-en-uno" element={<LandingOfertaTodoEnUno />} />
          
          {/* Client Portal Routes */}
          <Route path="/portal-cliente/login" element={<ClientPortalLogin />} />
          <Route path="/portal-cliente/dashboard" element={<ClientPortalDashboard />} />
          <Route path="/portal-cliente/facturas" element={<ClientPortalInvoices />} />
          <Route path="/portal-cliente/tickets" element={<ClientPortalTickets />} />
          <Route path="/portal-cliente/presupuestos" element={<ClientPortalQuotes />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <ConfirmDialogRoot />
    </div>
  );
}

export default App;
